<template>
    <div class="font-sans bg-neutrals_background text-neutrals_text min-h-screen flex flex-col">
        <!-- 🔹 HEADER -->
        <header class="bg-[#1a1a1a] text-white shadow sticky top-0 z-50">
            <div class="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                <router-link to="/" class="flex items-center gap-3">
                    <img src="/logo.png" alt="Logo" class="h-12 w-auto" />
                </router-link>

                <nav class="hidden md:flex space-x-8 text-sm font-medium">
                    <router-link to="/" class="hover:text-accent transition">Início</router-link>
                    <router-link to="/categorias" class="hover:text-accent transition">Categorias</router-link>
                    <router-link to="/contato" class="text-accent font-semibold">Contato</router-link>
                </nav>
            </div>
        </header>

        <!-- 🟡 HERO -->
        <section class="relative h-[240px] bg-cover bg-center flex items-center justify-center"
            style="background-image: url('/banner-marketplace.jpg')">
            <div class="absolute inset-0 bg-black/50"></div>
            <div class="relative z-10 text-center text-white px-4">
                <h1 class="text-3xl sm:text-4xl font-extrabold mb-2 drop-shadow">Fale Conosco</h1>
                <p class="text-base sm:text-lg opacity-90">Estamos prontos para te ajudar!</p>
            </div>
        </section>

        <!-- 📨 FORMULÁRIO -->
        <main class="flex-1 flex items-center justify-center py-16 px-4">
            <div class="bg-white rounded-2xl shadow-xl p-8 w-full max-w-lg border-t-4 border-accent">
                <h2 class="text-2xl font-bold text-primary mb-6 text-center">Envie sua mensagem</h2>

                <Form @submit="submitForm" :validation-schema="schema" class="space-y-4">
                    <!-- Nome -->
                    <div>
                        <label class="block text-sm font-medium text-neutrals_text mb-1">Nome</label>
                        <Field name="name" type="text"
                            class="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                            placeholder="Seu nome completo" />
                        <ErrorMessage name="name" class="text-red-600 text-xs mt-1 block" />
                    </div>

                    <!-- E-mail -->
                    <div>
                        <label class="block text-sm font-medium text-neutrals_text mb-1">E-mail</label>
                        <Field name="email" type="email"
                            class="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                            placeholder="seuemail@exemplo.com" />
                        <ErrorMessage name="email" class="text-red-600 text-xs mt-1 block" />
                    </div>

                    <!-- Assunto -->
                    <div>
                        <label class="block text-sm font-medium text-neutrals_text mb-1">Assunto</label>
                        <Field name="subject" type="text"
                            class="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                            placeholder="Ex: Dúvida sobre anúncio" />
                        <ErrorMessage name="subject" class="text-red-600 text-xs mt-1 block" />
                    </div>

                    <!-- Mensagem -->
                    <div>
                        <label class="block text-sm font-medium text-neutrals_text mb-1">Mensagem</label>
                        <Field as="textarea" name="message" rows="5"
                            class="w-full border rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none resize-none"
                            placeholder="Digite sua mensagem..." />
                        <ErrorMessage name="message" class="text-red-600 text-xs mt-1 block" />
                    </div>

                    <!-- Botão -->
                    <button type="submit" :disabled="loading"
                        class="w-full bg-primary text-white font-semibold py-3 rounded-lg hover:bg-secondary transition-all flex justify-center items-center gap-2">
                        <span v-if="!loading">Enviar Mensagem</span>
                        <svg v-else class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                            </circle>
                            <path class="opacity-75" fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                    </button>

                    <!-- Mensagens globais -->
                    <p v-if="successMessage" class="mt-4 text-center text-green-600 font-medium">
                        {{ successMessage }}
                    </p>
                    <p v-if="errorMessage" class="mt-4 text-center text-red-600 font-medium">
                        {{ errorMessage }}
                    </p>
                </Form>
            </div>
        </main>

        <!-- 🔻 RODAPÉ -->
        <AppFooter :enterprise="enterpriseData" />
        <Copyright :enterprise="enterpriseData" />
    </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import api from "@/services/api";
import AppFooter from "@/components/AppFooter.vue";
import Copyright from "@/components/Copyright.vue";

const loading = ref(false);
const successMessage = ref("");
const errorMessage = ref("");
const enterpriseData = ref({});

// Esquema de validação Yup
const schema = Yup.object({
    name: Yup.string().required("O nome é obrigatório."),
    email: Yup.string().email("Informe um e-mail válido.").required("O e-mail é obrigatório."),
    subject: Yup.string().required("O assunto é obrigatório."),
    message: Yup.string().min(3, "A mensagem deve ter no mínimo 3 caracteres.").required("A mensagem é obrigatória."),
});

async function submitForm(values, { resetForm }) {
    try {
        loading.value = true;
        successMessage.value = "";
        errorMessage.value = "";

        const response = await api.post("/send-contact-email", values);
        successMessage.value = "Mensagem enviada com sucesso! Entraremos em contato em breve.";
        resetForm();

    } catch (error) {
        errorMessage.value = "Ocorreu um erro ao enviar sua mensagem. Tente novamente mais tarde.";
        console.error(error);
    } finally {
        loading.value = false;
    }
}

onMounted(async () => {
    try {
        const response = await api.get("/enterprise/home");
        enterpriseData.value = response.data.data[0] || {};
    } catch (err) {
        console.error("Erro ao carregar dados da empresa:", err);
    }
});
</script>

<style scoped>
.bg-neutrals_background {
    background-color: #f6f4f1;
}

.text-primary {
    color: #152a34;
}

.bg-primary {
    background-color: #152a34;
}

.bg-secondary {
    background-color: #688075;
}

.border-accent {
    border-color: #b99a52;
}

.text-accent {
    color: #b99a52;
}
</style>
